/**
 * What `/practice?set=…` shows instead of the setup form (pedagogy spec §4.3.9, §7.3.5, §7.3.6): the
 * link was the student's "Starta", so the set starts by itself. It fetches the problems (`startSet`),
 * stores the set's state and moves on with `replace`, so Back from the run does not start a new set.
 * With nothing to serve it says so and leads to the plain setup page.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { Button, PageHeader, Spinner, useToast } from "@client/components/ui";
import { useT } from "@client/i18n";
import { messageForError } from "@client/lib/errors";
import { practiceFilters } from "@shared/practiceFilters";
import { useTopicsAndChapters } from "../api";
import { invalidPracticeLink } from "../links";
import { InvalidPracticeLink } from "../LectureSetup";
import { saveSession } from "../session";
import { savePlanSet } from "./planSet";
import { startSet, type SetPreset } from "./startSet";

type Phase = { kind: "starting" } | { kind: "empty" } | { kind: "failed"; message: string };

export interface SetStarterProps {
  preset: SetPreset;
}

export function SetStarter({ preset }: SetStarterProps) {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const taxonomy = useTopicsAndChapters();
  const filters = taxonomy.data
    ? practiceFilters(
        new URLSearchParams({ ...Object.fromEntries(params), mode: "mixed" }),
        taxonomy.data,
      )
    : undefined;
  const invalid = invalidPracticeLink(params, taxonomy.data);

  const [phase, setPhase] = useState<Phase>({ kind: "starting" });
  // Strict Mode runs effects twice in development; one link starts one set.
  const startedRef = useRef(false);
  // The fetch outlives the page: a student who pressed Back or a nav link meanwhile must not be
  // pulled into the set, have their history entry replaced or lose an unfinished session.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  async function run() {
    setPhase({ kind: "starting" });
    try {
      const started = await startSet({ ...preset, chapters: filters?.chapters ?? preset.chapters });
      if (!mountedRef.current) return;
      if (started.kind === "empty") {
        setPhase({ kind: "empty" });
      } else if (started.kind === "which-principle") {
        if (filters?.ignoredFilters.length)
          toast.show(t("practice.link.ignored", { filters: filters.ignoredFilters.join(", ") }));
        savePlanSet(started.data);
        navigate("/practice/principles", { replace: true });
      } else {
        if (filters?.ignoredFilters.length)
          toast.show(t("practice.link.ignored", { filters: filters.ignoredFilters.join(", ") }));
        saveSession(started.session);
        navigate("/practice/session", { replace: true });
      }
    } catch (error) {
      if (!mountedRef.current) return;
      setPhase({ kind: "failed", message: messageForError(error, t) });
    }
  }

  useEffect(() => {
    if (startedRef.current || invalid || (!taxonomy.data && !taxonomy.isError)) return;
    startedRef.current = true;
    void run();
    // `run` closes over the preset this component was mounted with; a link is read once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxonomy.data, taxonomy.isError, invalid]);

  if (invalid) return <InvalidPracticeLink />;
  const title =
    preset.set === "which-principle" ? t("practice.whichPrinciple.title") : t("practice.nav");
  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader title={title} />
      {filters && filters.ignoredFilters.length > 0 && (
        <p role="status">
          {t("practice.link.ignored", { filters: filters.ignoredFilters.join(", ") })}
        </p>
      )}
      {phase.kind === "starting" && <Spinner size="md" label={t("practice.set.starting")} />}
      {phase.kind === "empty" && (
        <p role="status" className="text-sm text-ink-700 dark:text-ink-200">
          {t(
            preset.set === "which-principle"
              ? "practice.whichPrinciple.empty"
              : "practice.timed.empty",
          )}
        </p>
      )}
      {phase.kind === "failed" && (
        <div className="space-y-3">
          <p role="alert" className="text-sm text-red-700 dark:text-red-300">
            {phase.message}
          </p>
          <Button variant="secondary" onClick={() => void run()}>
            {t("common.retry")}
          </Button>
        </div>
      )}
      {phase.kind !== "starting" && (
        <p className="mt-4 text-sm font-medium">
          <Link to="/practice" className="underline underline-offset-2 hover:no-underline">
            {t("practice.set.toSetup")}
          </Link>
        </p>
      )}
    </div>
  );
}
